import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { MakeModelLocationDefault } from './make-model-location-default';

const SAVINGS_DISPLAY_THRESHOLD = 250;

export function PricingHeaderUI({
  makeModelLocationComponent: MakeModelLocationComponent = MakeModelLocationDefault,
  params,
  vehicle,
  className = null,
  visitorLocation = null,
  noPricingData = false,
  estimatedSavingsTrim = null,
  onMakeModelChange = noop,
  disableTrademarkCharacter = true,
  isHighlights = false,
  isInternational = false,
  tag = 'p',
}) {
  let headerFragment;
  switch (true) {
    case isHighlights: {
      headerFragment = `${buildDisplayVehicle({
        makeModelSubmodelYear: vehicle,
        params,
        disableTrademarkCharacter,
      })} Trim Pricing`;
      break;
    }
    case noPricingData: {
      headerFragment = 'Pricing';
      break;
    }
    case estimatedSavingsTrim?.estimatedSavings >= SAVINGS_DISPLAY_THRESHOLD: {
      headerFragment = (
        <Fragment>
          Save as much as {getPriceString(estimatedSavingsTrim.estimatedSavings)}
          &nbsp;with Edmunds{' '}
          <PopoverItem iconClassName="size-16" screenReaderTooltipLabel="possible savings">
            Click on the trim you’re interested in to see what Edmunds suggests you should pay to get a good deal.
            Savings vary by trim; {getPriceString(estimatedSavingsTrim.estimatedSavings)} refers to estimated savings
            off of MSRP for the {estimatedSavingsTrim.trim} trim.
          </PopoverItem>
        </Fragment>
      );
      break;
    }
    default: {
      headerFragment = 'Compare dealer price quotes';
    }
  }
  const Tag = tag;

  return (
    <div className={classnames('pricing-header', className)}>
      <MakeModelLocationComponent
        makeModelSubmodelYear={vehicle}
        params={params}
        visitorLocation={visitorLocation}
        onMakeModelChange={onMakeModelChange}
        disableTrademarkCharacter={disableTrademarkCharacter}
        isHighlights={isHighlights}
        isInternational={isInternational}
      >
        <Tag className="heading-3 text-start mb-0_5">{headerFragment}</Tag>
      </MakeModelLocationComponent>
    </div>
  );
}

PricingHeaderUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  makeModelLocationComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  visitorLocation: VisitorEntities.Location,
  noPricingData: PropTypes.bool,
  estimatedSavingsTrim: PropTypes.shape({
    trim: PropTypes.string,
    estimatedSavings: PropTypes.number,
  }),
  onMakeModelChange: PropTypes.func,
  disableTrademarkCharacter: PropTypes.bool,
  isHighlights: PropTypes.bool,
  isInternational: PropTypes.bool,
  tag: PropTypes.string,
};

export const PricingHeader = React.memo(PricingHeaderUI);
